import styled from 'styled-components';
import { coreTokens } from '../../core';
import { useText } from '../../utils/hooks/use_text';
import { Icon } from '../icon';
import { theme } from './pagination.theme';

export interface PaginationProps {
	/**
	 * The current page, 1-indexed (`1` is the first page).
	 */
	currentPage: number;

	/**
	 * The total number of pages available.
	 */
	pageCount: number;

	/**
	 * Called with the requested page (1-indexed) when Previous, Next, or a
	 * page number is clicked.
	 */
	onPageChange: (page: number) => void;
}

function range(start: number, end: number): number[] {
	const length = end - start + 1;
	return Array.from({ length }, (_, i) => start + i);
}

/**
 * Builds the list of page numbers (and `'...'` truncation markers) to render,
 * given the current 1-indexed page and total page count. Always shows the
 * first and last page, plus one page on either side of the current page,
 * collapsing any gaps into an ellipsis.
 *
 * Mirrors the truncation algorithm used by Office's `Pagination` component.
 */
function paginationItems(page: number, count: number): (number | string)[] {
	const paginationSize = 1;
	const boundaryCount = 1;
	const firstPages = range(1, Math.min(boundaryCount, count));
	const lastPages = range(Math.max(count - boundaryCount + 1, boundaryCount + 1), count);

	const pagesStart = Math.max(Math.min(page - paginationSize, count - boundaryCount - paginationSize * 2 - 1), boundaryCount + 2);
	const pagesEnd = Math.min(
		Math.max(page + paginationSize, boundaryCount + paginationSize * 2 + 2),
		lastPages.length > 0 ? lastPages[0] - 2 : count - 1,
	);

	function startEllipsis(): (number | string)[] {
		if (pagesStart > boundaryCount + 2) return ['...'];
		if (boundaryCount + 1 < count - boundaryCount) return [boundaryCount + 1];
		return [];
	}

	function endEllipsis(): (number | string)[] {
		if (pagesEnd < count - boundaryCount - 1) return ['...'];
		if (count - boundaryCount > boundaryCount) return [count - boundaryCount];
		return [];
	}

	return [...firstPages, ...startEllipsis(), ...range(pagesStart, pagesEnd), ...endEllipsis(), ...lastPages];
}

const StyledContainer = styled.nav`
	box-sizing: border-box;
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: ${coreTokens.spacing.x1};
`;

const StyledArrowButton = styled.button`
	all: unset;
	box-sizing: border-box;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: ${coreTokens.spacing.x1};
	min-width: ${coreTokens.size.x5};
	height: ${coreTokens.size.x5};
	padding: 0 ${coreTokens.spacing.x1};
	border-radius: ${coreTokens.borderRadius.small};
	color: ${theme.getValue('iconColor')};
	font: ${theme.getValue('pageFont')};
	cursor: pointer;

	&:hover:not([aria-disabled='true']) {
		background: ${theme.getValue('iconHoverBackground')};
	}
	&:active:not([aria-disabled='true']) {
		background: ${theme.getValue('iconActiveBackground')};
	}
	&:focus-visible {
		outline: 2px solid ${theme.getValue('focusRingColor')};
		outline-offset: -2px;
	}
	&[aria-disabled='true'] {
		color: ${theme.getValue('iconDisabledColor')};
		cursor: default;
	}
`;

const StyledPages = styled.div`
	box-sizing: border-box;
	display: flex;
	flex: 1 1 auto;
	align-items: center;
	justify-content: center;
	gap: ${coreTokens.spacing.x1};
	min-width: 0;
`;

const StyledPageButton = styled.button`
	all: unset;
	box-sizing: border-box;
	display: flex;
	align-items: center;
	justify-content: center;
	min-width: ${coreTokens.size.x5};
	min-height: ${coreTokens.size.x5};
	border-radius: ${coreTokens.borderRadius.small};
	padding: ${coreTokens.spacing.x1};
	color: ${theme.getValue('iconColor')};
	font: ${theme.getValue('pageFont')};
	text-align: center;
	user-select: none;
	cursor: pointer;

	&:hover {
		background: ${theme.getValue('pageHoverBackground')};
	}
	&[aria-current='page'] {
		background: ${theme.getValue('pageActiveBackground')};
	}
	&:focus-visible {
		outline: 2px solid ${theme.getValue('focusRingColor')};
		outline-offset: -2px;
	}
`;

const StyledEllipsis = styled.span`
	box-sizing: border-box;
	display: flex;
	align-items: center;
	justify-content: center;
	min-width: ${coreTokens.size.x5};
	min-height: ${coreTokens.size.x5};
	color: ${theme.getValue('iconColor')};
	font: ${theme.getValue('pageFont')};
`;

/**
 * Numbered pagination controls: Previous/Next arrows plus page number
 * buttons, truncated with an ellipsis for large page counts. Automatically
 * renders nothing when `pageCount <= 1`.
 */
export function Pagination({ currentPage, pageCount, onPageChange }: PaginationProps): JSX.Element | null {
	const text = useText();

	if (pageCount <= 1) {
		return null;
	}

	const items = paginationItems(currentPage, pageCount);

	return (
		<StyledContainer>
			<StyledArrowButton
				type='button'
				onClick={() => {
					if (currentPage <= 1) return;
					onPageChange(currentPage - 1);
				}}
				aria-disabled={currentPage <= 1}
				aria-label={text('Component.Pagination.PreviousPage')}
			>
				<Icon type='arrowLeft' size='small' />
				{text('Component.Pagination.Previous')}
			</StyledArrowButton>
			<StyledPages>
				{items.map((item, index) =>
					typeof item === 'string' ? (
						<StyledEllipsis key={`ellipsis-${index}`} aria-hidden='true'>
							…
						</StyledEllipsis>
					) : (
						<StyledPageButton
							key={item}
							type='button'
							aria-current={item === currentPage ? 'page' : undefined}
							aria-label={text('Component.Pagination.Page', { page: String(item) })}
							onClick={() => item !== currentPage && onPageChange(item)}
						>
							{item}
						</StyledPageButton>
					),
				)}
			</StyledPages>
			<StyledArrowButton
				type='button'
				onClick={() => {
					if (currentPage >= pageCount) return;
					onPageChange(currentPage + 1);
				}}
				aria-disabled={currentPage >= pageCount}
				aria-label={text('Component.Pagination.NextPage')}
			>
				{text('Component.Pagination.Next')}
				<Icon type='arrowRight' size='small' />
			</StyledArrowButton>
		</StyledContainer>
	);
}
